import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { updatePassword } from "firebase/auth";
import userAdminService from '@/api/userAdmin.service';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

interface ForceChangePasswordModalProps {
  user: any;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
}

export const ForceChangePasswordModal = ({
  user,
  isModalOpen,
  setIsModalOpen,
}: ForceChangePasswordModalProps) => {
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleSubmit = async () => {
    if (passwordData.password !== passwordData.confirmPassword) {
        Swal.fire("Error", "Las contraseñas no coinciden", "error");
        return;
    }

    try {
      if (!user) throw new Error("No hay usuario autenticado");

      // 1. Update password in Firebase Auth
      await updatePassword(user, passwordData.password);

      // 2. Update Firestore status
      const updated = await userAdminService.updatePasswordResetStatus(user.uid);

      if (updated) {
        Swal.fire({
          title: "¡Contraseña actualizada!",
          text: "Su contraseña ha sido actualizada correctamente. Ahora puede acceder al panel.",
          icon: "success",
          timer: 2000,
        });
        setIsModalOpen(false);
        navigate('/dashboard');
      } else {
        throw new Error("No se pudo actualizar el estado en la base de datos");
      }
    } catch (error: any) {
      console.error(error);
      Swal.fire({
        title: "Error",
        text: error.message || "Hubo un problema al actualizar la contraseña. Posiblemente necesite iniciar sesión nuevamente.",
        icon: "error",
      });
    }
  };

  const isButtonDisabled = passwordData.password === '' || passwordData.password !== passwordData.confirmPassword;

  return (
    <Dialog open={isModalOpen} disableEscapeKeyDown>
      <DialogTitle sx={{ backgroundColor: "#3F51B5", color: "white" }}>
        Actualización Obligatoria de Contraseña
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="body1" gutterBottom pt={1}>
          Por seguridad, debe cambiar su contraseña la primera vez que inicia sesión.
        </Typography>
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <TextField
            required
            fullWidth
            label="Nueva Contraseña"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={passwordData.password}
            onChange={handleInputChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            required
            fullWidth
            label="Repetir Nueva Contraseña"
            name="confirmPassword"
            type="password"
            value={passwordData.confirmPassword}
            onChange={handleInputChange}
            error={passwordData.password !== passwordData.confirmPassword && passwordData.confirmPassword !== ''}
            helperText={passwordData.password !== passwordData.confirmPassword && passwordData.confirmPassword !== '' ? 'Las contraseñas no coinciden' : ''}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isButtonDisabled}
          sx={{ backgroundColor: "#3F51B5", color: "white", '&:hover': { backgroundColor: "#303F9F" } }}
          fullWidth
        >
          Actualizar y Entrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
